import { Stack } from '@mui/material'
import { useQueryClient } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { IGroupEntity } from '../../models'
import GroupsApiService from '../../services/groupsApiService'
import RationsApiService from '../../services/rationApiService'
import { useAppDispatch } from '../../store/hooks'
import { notifyUser } from '../../store/notificationsSlice'
import { ButtonUI } from '../../ui/buttons/ButtonUI'
import Drawer from '../../ui/Drawer'
import { TypographySecondary14 } from '../../ui/typography/TypographySecondary14'
import RationGroupsField from './RationGroupsField'

type RationActivateVersionDrawerProps = {
	open: boolean
	onClose: () => void
	rationId: number
	versionId: number
	versionDate?: string
	activeVersionId?: number
	onSuccess: () => void
}

const isSaved = (status: number) => status === 200 || status === 201

const RationActivateVersionDrawer = ({
	open,
	onClose,
	rationId,
	versionId,
	versionDate,
	activeVersionId,
	onSuccess,
}: RationActivateVersionDrawerProps) => {
	const dispatch = useAppDispatch()
	const queryClient = useQueryClient()
	const [selectedIds, setSelectedIds] = useState<number[]>([])
	const [saving, setSaving] = useState(false)

	useEffect(() => {
		if (!open) {
			setSelectedIds([])
			setSaving(false)
		}
	}, [open])

	const groupPayload = (
		group: IGroupEntity,
		nextRationId: number | null,
		rationTitle: string | null,
	) => ({
		id: group.key,
		title: group.title,
		description: group.description,
		cattles_qty: group.cattles_qty,
		cattles_limit: group.cattles_limit,
		weight_per_head: group.weight_per_head,
		ration_id: nextRationId,
		rationtitle: rationTitle,
		barn: group.barn,
		status: group.status,
	})

	const assignGroups = async () => {
		const cached = queryClient.getQueryData<IGroupEntity[]>([
			'ration-assign-groups',
		])
		const groups =
			cached ?? ((await GroupsApiService.getGroupList()).data as IGroupEntity[])
		const selected = (groups ?? []).filter(group =>
			selectedIds.includes(group.id),
		)
		if (selected.length !== selectedIds.length) {
			throw new Error('groups missing')
		}
		const results = await Promise.allSettled(
			selected.map(group =>
				GroupsApiService.putGroupsDataKey(
					groupPayload(group, rationId, group.rationtitle || null),
				).then(response => {
					if (!isSaved(response.status)) {
						throw new Error('group assign failed')
					}
				}),
			),
		)
		if (results.some(result => result.status === 'rejected')) {
			await Promise.allSettled(
				selected.map(group =>
					GroupsApiService.putGroupsDataKey(
						groupPayload(
							group,
							group.ration || null,
							group.rationtitle || null,
						),
					),
				),
			)
			throw new Error('group assign failed')
		}
	}

	const save = async () => {
		if (!selectedIds.length || saving) return
		setSaving(true)
		try {
			const activateResponse = await RationsApiService.activateRationVersion(
				rationId,
				versionId,
			)
			if (!isSaved(activateResponse.status)) {
				throw new Error('activate failed')
			}
			try {
				await assignGroups()
			} catch (assignError) {
				if (activeVersionId && activeVersionId !== versionId) {
					await RationsApiService.activateRationVersion(
						rationId,
						activeVersionId,
					).catch(() => undefined)
				}
				throw assignError
			}
			queryClient.invalidateQueries({ queryKey: ['ration-assign-groups'] })
			dispatch(
				notifyUser({
					title:
						versionDate && versionDate !== '—'
							? `Версия от ${versionDate} активирована`
							: 'Версия рациона активирована',
					description: '',
					isError: false,
				}),
			)
			onClose()
			onSuccess()
		} catch {
			dispatch(
				notifyUser({
					title: 'Произошла ошибка',
					description: 'Попробуйте снова.',
					isError: true,
				}),
			)
		} finally {
			setSaving(false)
		}
	}

	return (
		<Drawer
			open={open}
			onClose={onClose}
			title='Активация версии рациона'
			anchor='right'
			width={480}
			closeAboveTitle
		>
			<Stack
				sx={{
					flex: 1,
					minHeight: '100%',
					px: '24px',
					boxSizing: 'border-box',
				}}
			>
				<TypographySecondary14 sx={{ display: 'block' }}>
					Выберите группы, для которых будет активирован рацион. Текущие рационы
					в группах будут заменены.
				</TypographySecondary14>
				<Stack sx={{ mt: '24px' }}>
					<RationGroupsField
						selectedIds={selectedIds}
						onChange={setSelectedIds}
						showIntro={false}
						inputWidth='100%'
						stacked
					/>
				</Stack>
				<Stack sx={{ mt: 'auto', pb: '24px', pt: '24px' }}>
					<ButtonUI
						disabled={!selectedIds.length || saving}
						onClick={save}
						sx={{ height: '56px', width: '100%' }}
					>
						Сохранить
					</ButtonUI>
				</Stack>
			</Stack>
		</Drawer>
	)
}

export default RationActivateVersionDrawer
